import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { parseCodeBlock } from "./codeBlocks.js";

/* ═══════════════════════════════════════════
   NORTHSTAR ROADMAP EXPLORER
   Interactive timeline with live AI agent
   ═══════════════════════════════════════════ */

const API_URL = "https://northstar-program.com/.netlify/functions/perplexity-api";

// Telia brand palette — exact HEX values from the "Color values" reference
// in the Telia Brand Hub. The previous palette used off-brand near-blacks
// (#0F0520, #170930, #1E0D3D), off-brand light purples (#C77DFF, #D4A5FF)
// and an off-brand magenta (#E040FB). All replaced with primary palette.
const P = {
  // Surfaces — Deep Purple tone-on-tone
  bg:      "#29003E",                    // Telia Deep Purple
  surface: "#3D1556",                    // Deep Purple lifted (working surface)
  card:    "#4A1969",                    // Deep Purple lifted further (cards)
  border:  "rgba(244, 224, 255, 0.18)",  // Light Purple hairline

  // Brand accents
  purple:    "#990AE3",                  // Telia Core Purple (UI accent only)
  light:     "#F4E0FF",                  // Telia Light Purple (text on dark)
  accent:    "#F4E0FF",                  // alias for headings
  highlight: "#C23FE3",                  // Core Purple mid tint (sparingly)

  // Text
  white: "#FFFFFF",
  dim:   "rgba(244, 224, 255, 0.65)",    // muted Light Purple
  muted: "rgba(244, 224, 255, 0.4)",

  // Glow / focus
  glow:  "rgba(153, 10, 227, 0.4)",      // Core Purple glow

  // Information
  teal:  "#00827C",                      // Telia Teal (charts/info only)
};

// Telia Sans stack — loaded via @font-face in /css/styles.css. The
// previous file imported Outfit / DM Sans / JetBrains Mono from Google
// Fonts; that @import has been removed lower down.
const FF_HEADING = "'Telia Sans Heading', 'Telia Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const FF_BODY    = "'Telia Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// ── Roadmap Data ──
const YEARS = ["2025", "2026", "2027", "2028", "2029"];
// 5-step tonal sequence within the Telia primary + info palette.
// Each year gets a distinguishable on-brand hue. The previous YEAR_COLORS
// used a rainbow of off-brand Material accents (#B388FF, #E040FB, #00E5FF,
// #FFD740, #69F0AE) — none of which exist in the Telia palette.
const YEAR_COLORS = {
  "2025": "#F4E0FF",   // Light Purple
  "2026": "#C23FE3",   // Core Purple mid tint
  "2027": "#00827C",   // Teal
  "2028": "#990AE3",   // Core Purple
  "2029": "#D568F0",   // Core Purple light tint
};

// Categories — icons carry the meaning. All categories share the same
// Light Purple chrome instead of each having a unique hue, because the
// Telia palette doesn't support 9 distinct on-brand colours and Typography
// PDF p.8 prescribes hierarchy via weight/size/colour, not arbitrary
// palette inflation. The previous CATEGORIES list used 9 different
// off-brand Material accent colours.
const CATEGORIES = [
  { id: "orchestration", label: "Orchestration & assurance",  icon: "⚙️", color: P.light },
  { id: "apis",          label: "Network exposure / APIs",    icon: "🔌", color: P.light },
  { id: "location",      label: "Location-based services",    icon: "📍", color: P.light },
  { id: "edge",          label: "Edge compute & integration", icon: "☁️", color: P.light },
  { id: "security",      label: "Security",                   icon: "🛡️", color: P.light },
  { id: "uplink",        label: "Boost uplink",               icon: "📶", color: P.light },
  { id: "v2x",           label: "V2X & automotive",           icon: "🚗", color: P.light },
  { id: "latency",       label: "Low latency (L4S / TSN)",    icon: "⚡", color: P.light },
  { id: "other",         label: "Sensing, satellite & more",  icon: "🛰️", color: P.light },
];

const ROADMAP = {
  "2025": {
    qos: ["Basic slice/DNN/5QI service modelling"],
    apis: ["Telia API Gateway integration", "CAMARA Quality-on-Demand API", "CAMARA Positioning and tracking API"],
    location: ["Indoor dot positioning at ~1-2m precision", "GNSS RTK correction using 5G SA with cm level precision"],
    edge: ["Local and city-wide breakout deployment"],
    iot: ["RedCap for high througput and low battery usage"],
    uplink: ["UL CA + UL SU-MIMO (3tx)"],
    v2x: ["V2X slice using 5QI 79"],
    latency: ["Introduction of L4S for stable low-latency traffic streans"],
    other: ["SIB9 for 5G time sync"],
  },
  "2026": {
    qos: ["Advanced Slice/DNN/5QI service modelling"],
    apis: ["Geofencing CAMARA via ENL"],
    location: ["AI model for indoor positioning", "Outdoor positioning with TA & AoA (~10-50m)", "Passive 5G SA outdoor positioning"],
    edge: ["AI compute edge node integration", "More multi-tenant edge breakout nodes for larger areas"],
    iot: ["DRX and eDRX power saving for RedCap"],
    uplink: ["Uplink configured grant"],
    v2x: ["Connected digital traffic signage", "Other 5QIs for V2X"],
    latency: ["L4S E2E usage by first NorthStar partners"],
    other: ["ISAC/Sensing testing at Future by Lund", "Satellite as backhaul exploration"],
  },
  "2027": {
    qos: ["New templates for 5GAA and 5G ACIA", "URSP for Windows, iOS, Android"],
    apis: ["Seamless authentication via CAMARA and Secure Entitlement Server", "Connectivity insights via CAMARA"],
    location: ["Improved indoor positioning through AI", "Macro positioning tuned and more precise"],
    edge: ["Rollout of distributed AI+Edge nodes", "Scaling edge breakout to +40 city nodes"],
    iot: ["eRedCap for even more optimized battery usage"],
    uplink: ["Local use of mmWave depending on regulator decisions"],
    v2x: ["Edge discovery/handover for vehicles"],
    latency: ["L4S E2D deployment rollout to seeveral partners", "uRLLC enhancements (RAN)"],
    other: ["Early sensing + tracking ISAC scenarios", "Satellite as backhaul deployment", "Nomadic cell for NorthStar in underserved areas"],
  },
  "2028": {
    qos: ["Intent-based creation of network slices with appropriate settings including QoS"],
    apis: ["Network slice booking via CAMARA", "Connectivity insights via CAMARA"],
    location: ["High-accuracy cross-tech positioning (macro ↔ indoor ↔ RTK)", "Improved positioning using ISAC"],
    edge: ["Edge breakout scaling to +100 city nodes"],
    iot: ["RedCap and eRedCap device proliferation"],
    uplink: ["Expanded mmWave coverage depending on regulator decisions"],
    v2x: ["Remote control of vehicles on public road trials"],
    latency: ["TSN for manufacturing and smart grids (Ethernet PDUs) – 5G LAN", "Latency-prioritized scheduling for TSN"],
    other: ["ISAC deployed in multiple areas for public sector and defense", "NR NTN, IoT NTN early deployment"],
  },
  "2029": {
    qos: ["AI-based creation of network slices with appropriate settings including QoS"],
    apis: ["Expanding support for +20 CAMARA APIs"],
    location: ["Mature positioning using ISAC", "Leaking cable positioning exploration"],
    edge: ["Full NorthStar integration across AI + Edge + RAN"],
    iot: [],
    uplink: [],
    v2x: ["Autonomous vehicle corridor tests possible"],
    latency: ["Mature TSN ecosystem"],
    other: ["Mature ISAC ecosystem", "Vertical 5G coverage for aeronautics", "Full 5G/Satellite integration"],
  },
};

// ── Fenced blocks ──
// The panel runs on the Deep Purple surface, so these mirror the flow-diagram
// rules in css/styles.css with the dark palette instead of reusing them.
function CodeBlock({ code }) {
  return (
    <pre style={{
      margin: "16px 0", padding: "14px 16px",
      background: "rgba(41, 0, 62, 0.55)", border: `1px solid ${P.border}`,
      borderRadius: 10, whiteSpace: "pre-wrap", overflowWrap: "anywhere",
      fontFamily: FF_BODY, fontFeatureSettings: '"tnum" 1',
      fontSize: 13, lineHeight: 1.45, color: P.light,
    }}>{code}</pre>
  );
}

// Branch nodes are parts of, or alternatives under, the step carrying them —
// unnumbered and arrowless, so no sequence is implied between siblings.
function FlowBranch({ items }) {
  return (
    <ul style={{
      listStyle: "none", margin: "10px 0 0", padding: "0 0 0 12px",
      borderLeft: "2px solid rgba(153, 10, 227, 0.45)",
    }}>
      {items.map((child, i) => (
        <li key={i} style={{
          position: "relative", margin: i === items.length - 1 ? 0 : "0 0 6px",
          paddingLeft: 14, fontSize: 13, lineHeight: 1.45, color: P.light,
        }}>
          <span style={{
            position: "absolute", left: 0, top: "0.6em",
            width: 8, height: 2, background: "rgba(153, 10, 227, 0.8)",
          }} />
          {renderInline(child.text)}
          {child.label && (
            <span style={{ display: "block", marginTop: 2, fontSize: 12, color: P.dim }}>
              {child.label}
            </span>
          )}
          {child.children.length > 0 && <FlowBranch items={child.children} />}
        </li>
      ))}
    </ul>
  );
}

function FlowDiagram({ flow }) {
  const horizontal = flow.direction === "horizontal";

  return (
    <div style={{
      margin: "16px 0", padding: "16px 18px",
      background: "rgba(244, 224, 255, 0.05)", border: `1px solid ${P.border}`,
      borderRadius: 12,
    }}>
      <ol style={{
        listStyle: "none", margin: 0, padding: 0, display: "flex",
        flexDirection: horizontal ? "row" : "column",
        flexWrap: horizontal ? "wrap" : "nowrap",
        alignItems: horizontal ? "center" : "stretch",
        rowGap: horizontal ? 8 : 0,
      }}>
        {flow.steps.map((step, i) => {
          const card = (
            <span style={{
              display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0,
              // Vertical steps fill the column; horizontal ones size to content.
              flex: horizontal ? "0 1 auto" : "1 1 auto",
              padding: "12px 14px", background: "rgba(244, 224, 255, 0.07)",
              border: `1px solid ${P.border}`, borderLeft: `3px solid ${P.purple}`,
              borderRadius: 8, fontSize: 13.5, lineHeight: 1.4,
            }}>
              <span style={{
                flex: "0 0 auto", width: 22, height: 22, borderRadius: "50%",
                background: P.purple, color: P.white, fontSize: 12, fontWeight: 700,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>{i + 1}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 500, color: P.white }}>
                  {renderInline(step.text)}
                </span>
                {step.children.length > 0 && <FlowBranch items={step.children} />}
              </span>
            </span>
          );

          // Horizontal: the arrow travels inside its step's item, so a row that
          // wraps never leaves an arrow stranded at the end of the line.
          if (horizontal) {
            return (
              <li key={i} style={{
                display: "flex", alignItems: "center", gap: 8,
                flex: "0 1 auto", maxWidth: "100%",
              }}>
                {i > 0 && (
                  <span aria-hidden="true" style={{ color: P.highlight, fontSize: 18, lineHeight: 1 }}>→</span>
                )}
                {card}
              </li>
            );
          }

          return (
            <Fragment key={i}>
              {i > 0 && (
                <li role="presentation" style={{
                  display: "flex", alignItems: "center", gap: 10,
                  margin: "6px 0 6px 10px",
                }}>
                  <span aria-hidden="true" style={{
                    color: P.highlight, fontSize: 18, lineHeight: 1,
                    width: 22, textAlign: "center",
                  }}>↓</span>
                  {step.label && (
                    <span style={{
                      padding: "3px 12px", borderRadius: 999,
                      background: "rgba(153, 10, 227, 0.22)",
                      fontSize: 12, lineHeight: 1.35, color: P.light,
                    }}>{step.label}</span>
                  )}
                </li>
              )}
              <li style={{ display: "flex", minWidth: 0 }}>{card}</li>
            </Fragment>
          );
        })}
      </ol>
    </div>
  );
}

// ── Markdown-lite renderer ──
function RichText({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced block. Arrow chains and trees become flow diagrams; anything else
    // stays code. A block still streaming has no closing fence yet — take the
    // rest, so the reader never sees a bare ``` in the text.
    const fence = line.trim().match(/^```(.*)$/);
    if (fence) {
      const info = fence[1].trim();
      const body = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;

      const { code, flow } = parseCodeBlock(info ? `${info}\n${body.join("\n")}` : body.join("\n"));
      if (flow) {
        elements.push(<FlowDiagram key={key++} flow={flow} />);
      } else if (code) {
        elements.push(<CodeBlock key={key++} code={code} />);
      }
      continue;
    }

    // Detect markdown table: current line has pipes and next line is a separator (|---|)
    if (line.includes("|") && i + 1 < lines.length && /^\|?\s*[-:]+[-|:\s]+$/.test(lines[i + 1].trim())) {
      const tableLines = [];
      // Collect all contiguous table rows
      while (i < lines.length && lines[i].includes("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      // Parse header, skip separator, parse body rows
      const parseRow = (row) =>
        row.replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim());
      const header = parseRow(tableLines[0]);
      const bodyRows = tableLines.slice(2).filter(r => !/^\|?\s*[-:]+[-|:\s]+$/.test(r.trim())).map(parseRow);

      elements.push(
        <div key={key++} className="ai-rich-table" style={{ overflowX: "auto", margin: "16px 0" }}>
          <table style={{
            width: "100%", borderCollapse: "collapse", fontSize: 14,
            fontFamily: FF_BODY,
            background: "transparent", border: "none", margin: 0,
          }}>
            <thead>
              <tr style={{ background: "rgba(153, 10, 227, 0.16)" }}>
                {header.map((h, hi) => (
                  <th key={hi} style={{
                    textAlign: "left", padding: "10px 14px",
                    borderBottom: `2px solid ${P.purple}`,
                    borderTop: "none", borderLeft: "none", borderRight: "none",
                    color: P.light, fontWeight: 700, fontSize: 14,
                    letterSpacing: 0, background: "rgba(153, 10, 227, 0.16)",
                  }}>{renderInline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? "rgba(244, 224, 255, 0.05)" : "transparent" }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{
                      padding: "9px 14px",
                      borderBottom: `1px solid ${P.border}`,
                      borderTop: "none", borderLeft: "none", borderRight: "none",
                      color: P.light, lineHeight: 1.5,
                      fontSize: 14, background: "transparent",
                    }}>{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (line.startsWith("### ")) {
      // h4 = Bold (Typography PDF p.4)
      elements.push(<h4 key={key++} style={{
        color: P.light, fontSize: 15, fontWeight: 700,
        margin: "18px 0 6px", fontFamily: FF_BODY, textTransform: "none",
      }}>{line.slice(4)}</h4>);
    } else if (line.startsWith("## ")) {
      // h3 = Bold
      elements.push(<h3 key={key++} style={{
        color: P.light, fontSize: 17, fontWeight: 700,
        margin: "20px 0 8px", fontFamily: FF_BODY, textTransform: "none",
      }}>{line.slice(3)}</h3>);
    } else if (line.startsWith("# ")) {
      // h2 = Bold (this h is *inside* the AI panel, so it's a sub-heading
      // not the page-level Heading; Heading weight stays reserved for the
      // single page-level h1)
      elements.push(<h2 key={key++} style={{
        color: P.white, fontSize: 20, fontWeight: 700,
        margin: "20px 0 10px", fontFamily: FF_BODY, textTransform: "none",
      }}>{line.slice(2)}</h2>);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      elements.push(
        <div key={key++} style={{ display: "flex", gap: 8, marginBottom: 4, lineHeight: 1.5 }}>
          <span style={{ color: P.purple, flexShrink: 0, marginTop: 2 }}>◆</span>
          <span>{renderInline(line.slice(2))}</span>
        </div>
      );
    } else if (line.trim() === "") {
      elements.push(<div key={key++} style={{ height: 8 }} />);
    } else {
      elements.push(<p key={key++} style={{ margin: "4px 0", lineHeight: 1.5 }}>{renderInline(line)}</p>);
    }
    i++;
  }
  return <>{elements}</>;
}

function renderInline(text) {
  // Bold **text** — render as Medium weight (500), per Typography PDF p.6:
  // "Our Medium weight is used sparingly. When we want to emphasize or
  // highlight a single word or shorter phrase we use our Medium weight."
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} style={{ color: P.white, fontWeight: 500 }}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

// ── Streaming AI Panel ──
function AIPanel({ query, visible, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const fetchAnswer = useCallback(async () => {
    if (!query) return;
    setText("");
    setLoading(true);
    setError(null);

    try {
      abortRef.current = new AbortController();
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
        signal: abortRef.current.signal,
      });

      if (!res.ok) {
        throw new Error(`API returned ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            // Agent API typed SSE events; no [DONE] sentinel.
            const parsed = JSON.parse(data);
            if (parsed.type === "response.output_text.delta") {
              if (parsed.delta) {
                fullText += parsed.delta;
                setText(fullText);
              }
            } else if (
              parsed.type === "response.failed" ||
              parsed.type === "response.cancelled"
            ) {
              // Failed runs arrive over HTTP 200 - the event is the only signal.
              throw new Error(parsed.response?.error?.message || "Run failed");
            } else if (parsed.type === "error") {
              throw new Error(parsed.error?.message || "Stream error");
            }
          } catch (err) {
            if (err instanceof SyntaxError) continue; // skip malformed chunks
            throw err;
          }
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message);
        setLoading(false);
      }
    }
  }, [query]);

  useEffect(() => {
    if (visible && query) fetchAnswer();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [visible, query, fetchAnswer]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [text]);

  if (!visible) return null;

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, width: "min(520px, 90vw)", height: "100vh",
      background: P.surface, borderLeft: `1px solid ${P.border}`,
      zIndex: 200, display: "flex", flexDirection: "column",
      boxShadow: `-20px 0 60px rgba(0, 0, 0, 0.5)`,
      animation: "slideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
      fontFamily: FF_BODY,
    }}>
      {/* Header */}
      <div style={{
        padding: "20px 24px", borderBottom: `1px solid ${P.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0,
      }}>
        <div>
          {/* Eyebrow label — sentence case, no forced uppercase */}
          <div style={{
            fontFamily: FF_BODY, fontSize: 11, color: P.light,
            fontWeight: 500, letterSpacing: 0, marginBottom: 6,
            textTransform: "none",
          }}>NorthStar AI agent</div>
          <div style={{ fontSize: 14, color: P.light, lineHeight: 1.5, maxWidth: 380 }}>
            {query}
          </div>
        </div>
        <button onClick={() => { if (abortRef.current) abortRef.current.abort(); onClose(); }}
          aria-label="Close AI panel"
          style={{
            background: "rgba(244, 224, 255, 0.08)", border: `1px solid ${P.border}`,
            color: P.light, width: 32, height: 32, borderRadius: 999, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
            flexShrink: 0, fontFamily: FF_BODY,
          }}>✕</button>
      </div>

      {/* Content */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: "auto", padding: "20px 24px",
        fontSize: 14, color: P.light, fontFamily: FF_BODY, lineHeight: 1.5,
      }}>
        {loading && !text && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: P.dim }}>
            <div style={{ animation: "pulse 1.2s infinite", fontSize: 13 }}>Querying NorthStar agent…</div>
          </div>
        )}
        {error && (
          <div style={{
            background: "rgba(228, 23, 92, 0.12)", border: `1px solid rgba(228, 23, 92, 0.35)`,
            borderRadius: 12, padding: 16, fontSize: 13, color: P.light,
          }}>
            <strong style={{ fontWeight: 500 }}>Connection error:</strong> {error}
            <div style={{ marginTop: 8, color: P.dim, fontSize: 12 }}>
              The NorthStar agent at northstar-program.com may be temporarily unavailable. The roadmap data shown is from the latest planning meeting (Feb 2026).
            </div>
          </div>
        )}
        {text && <RichText text={text} />}
        {loading && text && (
          <span style={{ display: "inline-block", width: 6, height: 16, background: P.purple, marginLeft: 2, animation: "blink 0.8s infinite", verticalAlign: "text-bottom" }} />
        )}
      </div>

      {/* Footer — sentence case, no uppercase, no monospace */}
      <div style={{
        padding: "12px 24px", borderTop: `1px solid ${P.border}`,
        fontSize: 11, color: P.dim, fontFamily: FF_BODY,
        letterSpacing: 0, flexShrink: 0, textTransform: "none",
      }}>
        Powered by NorthStar AI · Perplexity Sonar Pro
      </div>
    </div>
  );
}

// ── Year Column ──
function YearColumn({ year, categories, data, onItemClick, activeItem }) {
  const color = YEAR_COLORS[year];
  const allItems = categories.flatMap(cat => (data[cat.id] || []).map(item => ({ ...cat, item })));
  const hasItems = allItems.length > 0;

  return (
    <div style={{ minWidth: 0 }}>
      {/* Year header */}
      <div style={{
        textAlign: "left", marginBottom: 20, position: "sticky", top: 0,
        background: P.bg, zIndex: 10, paddingBottom: 12, paddingTop: 4,
      }}>
        <div style={{
          fontFamily: FF_HEADING, fontSize: 36, fontWeight: 300,
          color: color, letterSpacing: -0.5, lineHeight: 1,
        }}>{year}</div>
        <div style={{
          height: 4, background: color, borderRadius: 2, marginTop: 10,
        }} />
      </div>

      {/* Items grouped by category */}
      {categories.map(cat => {
        const items = data[cat.id] || [];
        if (items.length === 0) return null;
        // All categories share the Light Purple chrome — meaning is carried
        // by the icon and label, not by per-category hue.
        return (
          <div key={cat.id} style={{
            marginBottom: 14,
            background: "rgba(244, 224, 255, 0.04)",
            border: `1px solid ${P.border}`,
            borderRadius: 12,
            padding: "14px 12px 8px",
            borderTop: `3px solid ${P.purple}`,
          }}>
            <div style={{
              fontSize: 12, fontFamily: FF_BODY, color: P.light,
              letterSpacing: 0, marginBottom: 10,
              display: "flex", alignItems: "center", gap: 8, padding: "0 4px 6px",
              textTransform: "none",
            }}>
              <span style={{ fontSize: 16 }}>{cat.icon}</span>
              <span style={{ fontWeight: 700, lineHeight: 1.2 }}>{cat.label}</span>
            </div>
            {items.map((item, j) => {
              const isActive = activeItem === `${year}-${cat.id}-${j}`;
              return (
                <button key={j}
                  onClick={() => onItemClick(year, cat, item, `${year}-${cat.id}-${j}`)}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: isActive ? "rgba(153, 10, 227, 0.18)" : "rgba(244, 224, 255, 0.05)",
                    border: isActive
                      ? `1px solid ${P.purple}`
                      : `1px solid rgba(244, 224, 255, 0.08)`,
                    borderRadius: 10, padding: "10px 12px", marginBottom: 8,
                    color: P.light,
                    fontSize: 13, lineHeight: 1.5, cursor: "pointer",
                    transition: "all 0.2s", fontFamily: FF_BODY,
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = "rgba(153, 10, 227, 0.1)";
                      e.currentTarget.style.borderColor = "rgba(153, 10, 227, 0.45)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = "rgba(244, 224, 255, 0.05)";
                      e.currentTarget.style.borderColor = "rgba(244, 224, 255, 0.08)";
                    }
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ color: P.purple, fontSize: 8, marginTop: 5, flexShrink: 0 }}>●</span>
                    <span>{item}</span>
                  </div>
                </button>
              );
            })}
          </div>
        );
      })}
      {!hasItems && (
        <div style={{
          textAlign: "center", color: P.dim, fontSize: 13,
          fontStyle: "italic", padding: 20, fontFamily: FF_BODY,
        }}>
          No items scheduled
        </div>
      )}
    </div>
  );
}

// ── Category Filter ──
function CategoryFilter({ categories, active, onToggle }) {
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: 8, padding: "12px 0",
    }}>
      {categories.map(cat => {
        const isOn = active.includes(cat.id);
        return (
          <button key={cat.id} onClick={() => onToggle(cat.id)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 999,
              background: isOn ? P.purple : "transparent",
              border: `1px solid ${isOn ? P.purple : P.border}`,
              color: isOn ? P.white : P.light,
              fontSize: 13, cursor: "pointer",
              fontWeight: isOn ? 500 : 400,
              transition: "all 0.2s", fontFamily: FF_BODY,
              whiteSpace: "nowrap", textTransform: "none",
            }}>
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Main App ──
export default function NorthStarRoadmap() {
  const [activeCategories, setActiveCategories] = useState(CATEGORIES.map(c => c.id));
  const [aiQuery, setAiQuery] = useState(null);
  const [aiVisible, setAiVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [customQuery, setCustomQuery] = useState("");

  const toggleCategory = (id) => {
    setActiveCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const filteredCategories = CATEGORIES.filter(c => activeCategories.includes(c.id));

  const handleItemClick = (year, cat, item, itemId) => {
    setActiveItem(itemId);
    const q = `Explain "${item}" in the NorthStar ${year} roadmap in detail. What is the technology, why is it important for NorthStar, what are 2-3 concrete use cases for Swedish industry, and what dependencies or prerequisites exist? Include how this differs from what public 5G networks in Sweden offer today.`;
    setAiQuery(q);
    setAiVisible(true);
  };

  const handleCustomQuery = () => {
    if (!customQuery.trim()) return;
    setActiveItem(null);
    setAiQuery(customQuery.trim());
    setAiVisible(true);
    setCustomQuery("");
  };

  const visibleYears = selectedYear ? [selectedYear] : YEARS;

  return (
    <div style={{
      width: "100%", minHeight: "100vh", background: P.bg,
      fontFamily: FF_BODY, color: P.light,
    }}>
      {/* Note: the @import url('https://fonts.googleapis.com/...') line that
          loaded Outfit / DM Sans / JetBrains Mono has been REMOVED.
          Telia Sans is loaded via @font-face in /css/styles.css. */}
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
        * { scrollbar-width: thin; scrollbar-color: ${P.purple}66 transparent; }
        *::-webkit-scrollbar { width: 6px; }
        *::-webkit-scrollbar-track { background: transparent; }
        *::-webkit-scrollbar-thumb { background: ${P.purple}66; border-radius: 3px; }
        ::selection { background: ${P.purple}55; color: ${P.white}; }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        padding: "28px 32px 0", borderBottom: `1px solid ${P.border}`,
        background: `linear-gradient(180deg, ${P.surface} 0%, ${P.bg} 100%)`,
      }}>
        {/* Top row: logos */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {/* Telia logo */}
            <img src="data:image/png;base64,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" alt="Telia" style={{ height: 36 }} />
            <span style={{ color: P.muted, fontSize: 16 }}>×</span>
            {/* Ericsson logo */}
            <img src="data:image/png;base64,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" alt="Ericsson" style={{ height: 36, filter: "brightness(0) invert(1)" }} />
          </div>
          {/* EU badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: 0.5 }}>
            <svg width="28" height="28" viewBox="0 0 28 28">
              <circle cx="14" cy="14" r="12" fill="#003399" />
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * 30 - 90) * Math.PI / 180;
                return <text key={i} x={14 + 9 * Math.cos(a)} y={14 + 9 * Math.sin(a)} textAnchor="middle" dominantBaseline="central" fontSize="5" fill="#FFD700">★</text>;
              })}
            </svg>
            <span style={{ fontSize: 9, color: P.muted, lineHeight: 1.2 }}>Co-funded by the<br />European Union</span>
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom: 16 }}>
          {/* Eyebrow — sentence case, Telia Sans Medium, no forced uppercase */}
          <div style={{
            fontFamily: FF_BODY, fontSize: 12, color: P.light,
            fontWeight: 500, letterSpacing: 0, marginBottom: 10,
            textTransform: "none",
          }}>Interactive roadmap explorer</div>
          {/* h1 uses the Heading weight (300) — Typography PDF p.3:
              "We use the Heading weight on our top-level headlines.
              ... It should preferably be used in one place in a layout/viewport." */}
          <h1 style={{
            fontFamily: FF_HEADING, fontSize: "clamp(28px, 4vw, 44px)",
            fontWeight: 300, margin: 0, letterSpacing: -0.5, lineHeight: 1,
            color: P.light, textTransform: "none",
          }}>
            NorthStar <span style={{ color: P.highlight }}>2025–2029</span>
          </h1>
          <p style={{
            fontSize: 15, color: P.light, marginTop: 12,
            lineHeight: 1.5, fontFamily: FF_BODY,
          }}>
            Click any roadmap item to query the NorthStar AI agent for detailed technology
            explanations, use cases, and comparisons with public 5G.
          </p>
        </div>

        {/* Year pills */}
        {/* Year pills */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <button onClick={() => setSelectedYear(null)} style={{
            padding: "8px 18px", borderRadius: 999, fontSize: 13, fontWeight: 500, cursor: "pointer",
            background: !selectedYear ? P.purple : "transparent",
            border: `1px solid ${!selectedYear ? P.purple : P.border}`,
            color: !selectedYear ? P.white : P.light,
            transition: "all 0.2s",
            fontFamily: FF_BODY, textTransform: "none",
          }}>All years</button>
          {YEARS.map(y => (
            <button key={y} onClick={() => setSelectedYear(selectedYear === y ? null : y)} style={{
              padding: "8px 18px", borderRadius: 999, fontSize: 13, fontWeight: 500, cursor: "pointer",
              background: selectedYear === y ? P.purple : "transparent",
              border: `1px solid ${selectedYear === y ? P.purple : P.border}`,
              color: selectedYear === y ? P.white : P.light,
              transition: "all 0.2s",
              fontFamily: FF_BODY, textTransform: "none",
            }}>{y}</button>
          ))}
        </div>

        {/* Category filter */}
        <CategoryFilter categories={CATEGORIES} active={activeCategories} onToggle={toggleCategory} />
      </div>

      {/* ── Custom query bar ── */}
      <div style={{
        padding: "16px 32px", borderBottom: `1px solid ${P.border}`,
        display: "flex", gap: 10, alignItems: "center",
      }}>
        <div style={{
          flex: 1, display: "flex", alignItems: "center", gap: 10,
          background: "rgba(244, 224, 255, 0.05)", border: `1px solid ${P.border}`,
          borderRadius: 999, padding: "0 18px",
        }}>
          <span style={{ color: P.light, fontSize: 14 }}>🔍</span>
          <input
            value={customQuery}
            onChange={e => setCustomQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleCustomQuery()}
            placeholder="Ask the NorthStar agent anything about the roadmap…"
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: P.light, fontSize: 14, padding: "13px 0",
              fontFamily: FF_BODY,
            }}
          />
        </div>
        {/* Primary button — solid Core Purple, the one place Core Purple
            is permitted as a background per Colors PDF p.6.
            Removed the broken linear-gradient(P.purple, P.magenta) — magenta
            no longer exists in the brand-compliant palette. */}
        <button onClick={handleCustomQuery} style={{
          padding: "13px 24px", borderRadius: 999,
          background: P.purple,
          border: "none", color: P.white, fontSize: 14, fontWeight: 500,
          cursor: "pointer", transition: "background-color 0.15s, transform 0.15s, box-shadow 0.15s",
          fontFamily: FF_BODY, whiteSpace: "nowrap", textTransform: "none",
        }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "#3D1556";
            e.currentTarget.style.color = P.light;
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = `0 6px 20px ${P.glow}`;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = P.purple;
            e.currentTarget.style.color = P.white;
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >Ask agent</button>
      </div>

      {/* ── Roadmap Grid ── */}
      <div style={{
        padding: "24px 32px",
        display: "grid",
        gridTemplateColumns: visibleYears.length === 1
          ? "1fr"
          : `repeat(${visibleYears.length}, minmax(260px, 1fr))`,
        gap: 16,
        overflowX: "auto",
        minHeight: "calc(100vh - 340px)",
        transition: "all 0.3s",
        marginRight: aiVisible ? "min(520px, 90vw)" : 0,
      }}>
        {visibleYears.map(year => (
          <YearColumn
            key={year}
            year={year}
            categories={filteredCategories}
            data={ROADMAP[year]}
            onItemClick={handleItemClick}
            activeItem={activeItem}
          />
        ))}
      </div>

      {/* ── AI Panel ── */}
      <AIPanel
        query={aiQuery}
        visible={aiVisible}
        onClose={() => { setAiVisible(false); setActiveItem(null); }}
      />
    </div>
  );
}
